import { useLang } from '@/i18n';
import FormatCurrencyLocal from 'eco-design-reliance/currency/FormatCurrencyLocal';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import { useRouter } from 'next/router';
import { BundleItemType } from '../data';
import css from './index.module.scss';

export default function ProductItem({
    item,
    callback,
}: {
    item: BundleItemType;
    callback: (parentVariantId: string, variantId: string, type: 'plus' | 'minus') => void;
}) {
    const { t } = useLang('Bundles');
    const { locale = '' } = useRouter();
    const [MIN, MAX] = [0, 100];
    const {
        type = '',
        variantImg = '',
        img = '',
        name = '',
        productTitle = '',
        skuTitle = '',
        power = '',
        desc = '',
        subs,
        price,
        compareAtPrice,
        inventoryQuantity,
        inventoryPolicy,
        tip = '',
        link_buy = '',
    } = item;

    const getDynamicLinkBuy = (item: BundleItemType): string => {
        let link = `https://${locale}.ecoflow.com/cart/${item.variantId}:1`;
        if (item?.subs && item?.subs?.length > 0) {
            const hasQuantiySubs = item?.subs?.filter((sub: BundleItemType) => sub?.quantity && typeof sub?.quantity === 'number' && sub?.quantity > 0);
            hasQuantiySubs?.map((sub: BundleItemType, i: number) => {
                if (i < hasQuantiySubs?.length) {
                    link += ',';
                }
                link += `${sub?.variantId}:${sub?.quantity}`;
            });
        }
        link += `?channel=buy_button`;
        return link;
    };

    const handleCalc = (parentVariantId: string, variantId: string, type: 'plus' | 'minus', disabled = false) => {
        if (disabled) return;
        callback(parentVariantId, variantId, type);
    };

    return (
        <div className={`${css['bundle-item']} bundle-item ${item.type === 'customize' ? 'customize-item' : ''}`}>
            {type === 'customize' ? (
                <>
                    <div className="cus-info">
                        <p className="cus-title">{productTitle || skuTitle}</p>
                        <EcoLink
                            className="cus-link GtmModuleData-btn-href"
                            href={link_buy}
                            target="_blank"
                            dataLayer={{
                                __btn_id1: css['bundle-item'],
                                __btn_id2: link_buy,
                            }}
                        >
                            <span>{t('item_more')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28" fill="none">
                                <g clipPath="url(#clip0_4834_25090)">
                                    <path
                                        d="M14.5 26C21.1274 26 26.5 20.6274 26.5 14C26.5 7.37258 21.1274 2 14.5 2C7.87258 2 2.5 7.37258 2.5 14C2.5 20.6274 7.87258 26 14.5 26Z"
                                        fill="#3F68E0"
                                    />
                                    <path
                                        d="M12.7174 19.2832L17.5 14.5006L17.5877 14.4132L12.6959 9.52148"
                                        stroke="white"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_4834_25090">
                                        <rect width="28" height="28" fill="white" transform="translate(0.5)" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </EcoLink>
                    </div>
                    <EcoImg className="cus-img" pc={img} />
                </>
            ) : (
                <>
                    <div className="item-info">
                        <EcoImg className="item-img" pc={variantImg || img} />
                        <p className="item-title">{name}</p>
                        {power && <p className="item-power">{power}</p>}
                        {desc && <p className="item-desc">{desc}</p>}
                        {subs && subs?.length > 0 && (
                            <>
                                <p className="item-subs-label">{t('subs_label')}</p>
                                <ul className="item-subs">
                                    {subs?.map((sub: BundleItemType) => (
                                        <li key={sub?.variantId} className="item-sub">
                                            <EcoImg
                                                className="sub-img"
                                                key={`sub-${sub?.parentVariantId}-${sub?.variantId}`}
                                                pc={sub?.variantImg || sub?.img}
                                            />
                                            <div className="sub-info">
                                                <p className="sub-title">
                                                    {sub?.skuTitle && sub?.skuTitle !== 'Default Title' ? sub?.skuTitle : sub?.productTitle}
                                                </p>
                                                <div className="sub-cont">
                                                    <div className="sub-price">
                                                        {typeof sub?.price === 'number' && sub?.price >= 0 && (
                                                            <span className="sub-price-price">{FormatCurrencyLocal(sub?.price, locale)}</span>
                                                        )}
                                                        {sub?.compareAtPrice &&
                                                            typeof sub?.compareAtPrice === 'number' &&
                                                            sub?.compareAtPrice >= 0 &&
                                                            sub?.compareAtPrice !== sub?.price && (
                                                                <span className="sub-price-compare">{FormatCurrencyLocal(sub?.compareAtPrice, locale)}</span>
                                                            )}
                                                    </div>
                                                    <div className="sub-calc">
                                                        {Number(sub?.quantity) <= MIN ? (
                                                            <svg
                                                                className={`minus calc-icon disabled`}
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                onClick={() => handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'minus', true)}
                                                            >
                                                                <g opacity="0.4">
                                                                    <path
                                                                        d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3ZM7.75781 11.2002C7.3436 11.2002 7.00781 11.536 7.00781 11.9502C7.00805 12.3642 7.34375 12.6992 7.75781 12.6992H16.2422C16.6563 12.6992 16.9919 12.3642 16.9922 11.9502C16.9922 11.536 16.6564 11.2002 16.2422 11.2002H7.75781Z"
                                                                        fill="#18191B"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className={`minus calc-icon`}
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                onClick={() => handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'minus')}
                                                            >
                                                                <path
                                                                    d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3ZM7.75781 11.2002C7.3436 11.2002 7.00781 11.536 7.00781 11.9502C7.00805 12.3642 7.34375 12.6992 7.75781 12.6992H16.2422C16.6563 12.6992 16.9919 12.3642 16.9922 11.9502C16.9922 11.536 16.6564 11.2002 16.2422 11.2002H7.75781Z"
                                                                    fill="#18191B"
                                                                />
                                                            </svg>
                                                        )}
                                                        <span className="calc-text">{sub.quantity}</span>

                                                        {Number(sub?.quantity) >=
                                                        (sub?.inventoryPolicy === 'continue' ? MAX : Math.min(sub?.inventoryQuantity || 0, MAX)) ? (
                                                            <svg
                                                                className={`plus calc-icon disabled`}
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                onClick={() => handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'plus', true)}
                                                            >
                                                                <g opacity="0.4">
                                                                    <path
                                                                        d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                                                                        fill="black"
                                                                    />
                                                                    <path
                                                                        d="M12.0137 6.85742C12.4277 6.85768 12.7627 7.19337 12.7627 7.60742V11.1992H16.3428C16.7567 11.1994 17.0916 11.5353 17.0918 11.9492C17.0918 12.3633 16.7568 12.699 16.3428 12.6992H12.7627V16.292C12.7625 16.7059 12.4275 17.0408 12.0137 17.041C11.5996 17.041 11.2639 16.706 11.2637 16.292V12.6992H7.6582C7.24399 12.6992 6.9082 12.3634 6.9082 11.9492C6.90835 11.5351 7.24408 11.1992 7.6582 11.1992H11.2637V7.60742C11.2637 7.19321 11.5995 6.85742 12.0137 6.85742Z"
                                                                        fill="white"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className={`plus calc-icon`}
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                onClick={() => handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'plus')}
                                                            >
                                                                <path
                                                                    d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                                                                    fill="black"
                                                                />
                                                                <path
                                                                    d="M12.0137 6.85742C12.4277 6.85768 12.7627 7.19337 12.7627 7.60742V11.1992H16.3428C16.7567 11.1994 17.0916 11.5353 17.0918 11.9492C17.0918 12.3633 16.7568 12.699 16.3428 12.6992H12.7627V16.292C12.7625 16.7059 12.4275 17.0408 12.0137 17.041C11.5996 17.041 11.2639 16.706 11.2637 16.292V12.6992H7.6582C7.24399 12.6992 6.9082 12.3634 6.9082 11.9492C6.90835 11.5351 7.24408 11.1992 7.6582 11.1992H11.2637V7.60742C11.2637 7.19321 11.5995 6.85742 12.0137 6.85742Z"
                                                                    fill="white"
                                                                />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                    <div className="item-cont">
                        <div className="item-price">
                            {typeof price === 'number' && price >= 0 && <span className="item-price-price">{FormatCurrencyLocal(price, locale)}</span>}
                            {compareAtPrice && typeof compareAtPrice === 'number' && compareAtPrice >= 0 && compareAtPrice !== price && (
                                <span className="item-price-compare">{FormatCurrencyLocal(compareAtPrice, locale)}</span>
                            )}
                            {tip && <p className="item-tip">{tip}</p>}
                        </div>
                        {inventoryPolicy === 'continue' || (typeof inventoryQuantity === 'number' && inventoryQuantity > 0) ? (
                            <EcoLink
                                href={getDynamicLinkBuy(item)}
                                className="item-btn GtmModuleData-btn-href"
                                target="_blank"
                                dataLayer={{
                                    __btn_id1: css['bundle-item'],
                                    __btn_id2: getDynamicLinkBuy(item),
                                }}
                            >
                                {t('item_buy')}
                            </EcoLink>
                        ) : (
                            <button disabled className="item-btn disabled GtmModuleData-btn-text">
                                {t('item_sold_out')}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
